import React from 'react';
import PropTypes from 'prop-types';
import {Tag} from '@/components/atoms';
import {TAG_COLOR} from '@/utils/constants/colors';

const IncludedCell = ({included}) => {
  return (
    <Tag
      color={included ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150}
      text={!included ? 'Exclude' : 'Include'}
      isCopyable={false}
    />
  );
};

IncludedCell.propTypes = {
  included: PropTypes.bool.isRequired
};

const ReadOnlyReviewTable = ({
  products = [],
  columns = [
    {key: 'productName', header: 'Name'},
    {key: 'productId', header: 'Product Id'},
    {key: 'status', header: 'Status'},
    {key: 'size', header: 'Size'},
    {key: 'category', header: 'Category'},
    {key: 'brand', header: 'Brand'},
    {key: 'included', header: 'Include/Exclude', isCustom: true}
  ],
  className = '',
  tableClassName = '',
  headerStyle = {},
  cellStyle = {},
  onRowClick = null
}) => {
  const renderCell = (product, column) => {
    if (column.isCustom && column.key === 'included') {
      return <IncludedCell included={product.included} />;
    }
    return product[column.key] || '';
  };

  return (
    <div className={`products-table ${className}`}>
      <table
        className={`responsive-table w-full ${tableClassName}`}
        style={{backgroundColor: 'var(--color-background-layer-01)'}}>
        <thead
          className='sticky top-0 z-10'
          style={{
            backgroundColor:
              'var(--color-background-layer-02, var(--color-background-layer-01))',
            ...headerStyle
          }}>
          <tr style={{boxShadow: 'none'}}>
            {columns.map((column) => (
              <th
                key={column.key}
                className='column-header text-left'
                style={{
                  position: 'relative',
                  width: '150px',
                  padding: '12px 8px',
                  color: 'var(--color-text-primary, #ffffff)',
                  fontWeight: '600',
                  borderBottom: '1px solid var(--color-border-subtle)'
                }}>
                <div
                  className='datatable-header-separator'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    minHeight: '32px'
                  }}>
                  <div
                    className='header-container'
                    style={{padding: '4px 8px'}}>
                    {column.header}
                  </div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => (
            <tr
              key={`${product.productId}-${index}`}
              className={`single-selection-row row-small ${onRowClick ? 'cursor-pointer' : ''}`}
              style={{
                borderBottom: '1px solid var(--color-border-subtle)'
              }}
              onClick={
                onRowClick ? () => onRowClick(product, index) : undefined
              }
              aria-expanded='false'>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className='column-cell'
                  style={{
                    width: '150px',
                    padding: '12px 8px',
                    color:
                      column.key === 'included'
                        ? 'inherit'
                        : 'var(--color-text-primary, #ffffff)',
                    ...cellStyle
                  }}>
                  {column.key === 'productName' ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                      {renderCell(product, column)}
                    </div>
                  ) : (
                    renderCell(product, column)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

ReadOnlyReviewTable.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      productName: PropTypes.string,
      productId: PropTypes.string,
      status: PropTypes.string,
      size: PropTypes.string,
      category: PropTypes.string,
      brand: PropTypes.string,
      included: PropTypes.bool
    })
  ).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      isCustom: PropTypes.bool
    })
  ),
  className: PropTypes.string,
  tableClassName: PropTypes.string,
  headerStyle: PropTypes.object,
  cellStyle: PropTypes.object,
  onRowClick: PropTypes.func
};

export default React.memo(ReadOnlyReviewTable);
